import React, { useState, useEffect } from "react";
import {
  CardBtn,
  CardDateText,
  CardHeaderDiv,
  CardHeaderText,
  CardMainDiv,
} from "./style";
import { Btn, FlexDiv } from "../../assets/styles/style";
import { useMediaQuery } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Delete from "../../assets/images/DeleteIcon.svg";
import toast from "react-hot-toast";

const MT_Card = ({
  id,
  headerText = "Mock Test - 1",
  dateText = "Date Taken: 01/12/24",
  onDelete,
  analyticsPath,
  feedbackPath,
  viewScorePath,
  show = true,
  status = "completed", // Add status prop
  testType = "Full", // Add testType prop to determine Full vs Sectional
}) => {
  const isTab = useMediaQuery("(max-width:750px)");
  const navigate = useNavigate();
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [isTextVisible, setIsTextVisible] = useState(true);

  const handleLockedClick = () => {
    toast.error("This score is locked. Please subscribe to access."); 
  };

  // Check if test is pending (score being calculated)
  const isPending = status === "pending";

  // Array of engaging messages for pending tests based on test type
  const getPendingMessages = () => {
    if (testType === "Full") {
      return [
        "AI Scoring Engine is analyzing your Full Mock Test responses...",
        "Grading Speaking: Read Aloud, Describe Image, Re-tell Lecture & more...",
        "Scoring Writing: Essay, Summarize Written Text & key tasks...",
        "Analyzing Reading: Fill in the Blanks, Re-order Paragraphs & more...",
        "Evaluating Listening: Summarize Spoken Text, Highlight Incorrect Words & key tasks...",
        "Benchmarking your scores against the Global PTE Scale for all four skills...",
        "Analyzing Enabling Skills: Grammar, Fluency, Pronunciation, Spelling, Vocabulary...",
        "Calculating your overall score: Listening, Reading, Speaking, and Writing...",
        "Generating comprehensive skill profiles and detailed feedback reports...",
        "Almost there! Your detailed Full Mock Test score report is being compiled...",
        "This comprehensive analysis ensures your score is accurate and fair across all test sections...",
        "Thank you for your patience. Your detailed Full Mock Test results will be ready shortly."
      ];
    } else {
      if (testType === "Speaking") {
        return [
          "AI Scoring Engine is analyzing your Speaking Section responses...",
          "Processing Read Aloud: evaluating Content, Pronunciation, and Fluency...",
          "Analyzing Repeat Sentence: assessing Content and Pronunciation accuracy...",
          "Evaluating Describe Image: scoring Content, Pronunciation, and Fluency...",
          "Processing Re-tell Lecture: analyzing Content, Pronunciation, and Fluency...",
          "Assessing Answer Short Question: evaluating Content accuracy...",
          "Analyzing Summarize Group Discussion: scoring Content, Pronunciation, and Fluency...",
          "Processing Respond to a situation: evaluating Content and Pronunciation...",
          "Benchmarking your Speaking scores against the Global PTE Scale...",
          "Analyzing Speaking Enabling Skills: Oral Fluency, Pronunciation...",
          "Generating your personalized Speaking feedback report...",
          "Almost there! Your Speaking Section score report is being compiled..."
        ];
      } else if (testType === "Writing") {
        return [
          "AI Scoring Engine is analyzing your Writing Section responses...",
          "Processing Summarize Written Text: evaluating Content, Form, Grammar, and Vocabulary...",
          "Analyzing Write Essay: scoring Content, Form, Development, Structure, Grammar, Vocabulary, and Spelling...",
          "Evaluating Summarize Spoken Text: assessing Content, Form, Grammar, and Vocabulary...",
          "Processing Write from Dictation: scoring Content, Form, Grammar, Vocabulary, and Spelling...",
          "Benchmarking your Writing scores against the Global PTE Scale...",
          "Analyzing Writing Enabling Skills: Grammar, Vocabulary, Spelling, Written Discourse...",
          "Generating your personalized Writing feedback report...",
          "Almost there! Your Writing Section score report is being compiled..."
        ];
      } else if (testType === "Reading") {
        return [
          "AI Scoring Engine is analyzing your Reading Section responses...",
          "Processing Reading & Writing: Fill in the Blanks: evaluating Reading and Writing skills...",
          "Analyzing Multiple Choice, Multiple Answers: assessing Reading comprehension...",
          "Evaluating Re-order Paragraphs: scoring Reading skills...",
          "Processing Reading: Fill in the Blanks: assessing Reading skills...",
          "Analyzing Multiple Choice, Single Answer: evaluating Reading comprehension...",
          "Assessing Highlight Correct Summary: scoring Reading skills...",
          "Processing Highlight Incorrect Words: evaluating Reading skills...",
          "Benchmarking your Reading scores against the Global PTE Scale...",
          "Analyzing Reading Enabling Skills: Vocabulary, Written Discourse...",
          "Generating your personalized Reading feedback report...",
          "Almost there! Your Reading Section score report is being compiled..."
        ];
      } else if (testType === "Listening") {
        return [
          "AI Scoring Engine is analyzing your Listening Section responses...",
          "Processing Repeat Sentence: evaluating Content and Pronunciation...",
          "Analyzing Re-tell Lecture: scoring Content, Pronunciation, and Fluency...",
          "Evaluating Answer Short Question: assessing Content accuracy...",
          "Processing Summarize Group Discussion: scoring Content, Pronunciation, and Fluency...",
          "Analyzing Summarize Spoken Text: evaluating Content, Form, Grammar, and Vocabulary...",
          "Assessing Listening: Multiple Choice, Multiple Answers: scoring Listening skills...",
          "Processing Fill in the Blanks: evaluating Listening skills...",
          "Analyzing Highlight Correct Summary: scoring Listening skills...",
          "Evaluating Highlight Incorrect Words: assessing Listening skills...",
          "Processing Listening: Multiple Choice, Single Answer: scoring Listening skills...",
          "Analyzing Select Missing Word: evaluating Listening skills...",
          "Processing Write from Dictation: scoring Content, Form, Grammar, Vocabulary, and Spelling...",
          "Benchmarking your Listening scores against the Global PTE Scale...",
          "Generating your personalized Listening feedback report...",
          "Almost there! Your Listening Section score report is being compiled..."
        ];
      } else {
        // Default sectional messages
        return [
          "AI Scoring Engine is analyzing your Sectional Mock Test responses...",
          "Processing your answers for Content, Pronunciation, and Fluency...",
          "Benchmarking your scores against the Global PTE Scale...",
          "Analyzing Enabling Skills: Grammar, Oral Fluency, Pronunciation, Spelling, Vocabulary, Written Discourse...",
          "Generating your personalized feedback report...",
          "Almost there! Your detailed score report is being compiled...",
          "This detailed analysis ensures your score is accurate and fair...",
          "Thank you for your patience. Your results will be ready shortly."
        ];
      }
    }
  };

  const pendingMessages = getPendingMessages();

  // Rotate through messages every 5 seconds with fade effect
  useEffect(() => {
    if (isPending) {
      const interval = setInterval(() => {
        // Fade out current text
        setIsTextVisible(false);
        
        // After fade out, change text and fade in
        setTimeout(() => {
          setCurrentTextIndex((prevIndex) => 
            (prevIndex + 1) % pendingMessages.length
          );
          setIsTextVisible(true);
        }, 300); // 300ms fade out duration
      }, 5000); // Changed from 3000ms to 5000ms (5 seconds)

      return () => clearInterval(interval);
    }
  }, [isPending, pendingMessages.length]);

  return (
    <>
      <CardHeaderDiv style={{ 
        opacity: show ? "1" : "0.5",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
      }}>
        <CardHeaderText>{headerText}</CardHeaderText>
        {isPending && (
          <div style={{
            background: "#996CFE",
            color: "white",
            padding: "4px 8px",
            borderRadius: "12px",
            fontSize: "10px",
            fontWeight: "600",
            textTransform: "uppercase",
            letterSpacing: "0.5px"
          }}>
            Pending
          </div>
        )}
      </CardHeaderDiv>
      <CardMainDiv style={{
        backgroundColor: "white" // Keep white background as before
      }}>
        <CardDateText style={{ color: !show ? "#ccc" : "" }} >{dateText}</CardDateText>
        
        {isPending ? (
          // Show pending message with loader
          <FlexDiv style={{ 
            justifyContent: "space-between", 
            alignItems: "center",
            marginRight: isTab ? "" : "1.5rem",
            gap: "1rem"
          }}>
            <div style={{ 
              color: "#996CFE", 
              fontSize: "14px", 
              fontWeight: "500",
              fontStyle: "italic",
              opacity: isTextVisible ? 1 : 0,
              transition: "opacity 0.3s ease-in-out"
            }}>
              {pendingMessages[currentTextIndex]}
            </div>
            <div style={{ 
              width: "20px", 
              height: "20px", 
              border: "2px solid #f3f3f3",
              borderTop: "2px solid #996CFE",
              borderRadius: "50%",
              animation: "spin 1s linear infinite"
            }}></div>
          </FlexDiv>
        ) : (
          // Show normal buttons for completed tests
          <FlexDiv
            style={{
              gap: "1rem",
              marginRight: isTab ? "" : "1.5rem",
              flexDirection: isTab ? "column" : "",
            }}
          >
            <FlexDiv style={{ gap: "1rem" }}>
              <CardBtn
                onClick={show ? () => navigate(analyticsPath) : handleLockedClick}
                disabled={!show} // Disable the button if locked
                style={{ backgroundColor: !show ? "#ccc" : "" }}
              >
                Analytics
              </CardBtn>
              <Btn>
                <CardBtn
                  onClick={
                    show ? () => navigate(feedbackPath) : handleLockedClick
                  }
                  disabled={!show}
                  style={{ backgroundColor: !show ? "#ccc" : "" }}
                >
                  Score Feedback
                </CardBtn>
              </Btn>
            </FlexDiv>
            <FlexDiv style={{ gap: "1rem" }}>
              <CardBtn
                onClick={show ? () => navigate(viewScorePath) : handleLockedClick}
                disabled={!show} // Disable the button if locked
                style={{ backgroundColor: !show ? "#ccc" : "" }}
              >
                View Score
              </CardBtn>
              <CardBtn
                style={{
                  background: "var(--Accents-Strawberry-Red, #DB3031)",
                  width: "5rem",
                }}
                onClick={onDelete}
              >
                <img alt="" src={Delete} />
              </CardBtn>
            </FlexDiv>
          </FlexDiv>
        )}
      </CardMainDiv>
      
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default MT_Card;
